import { useEffect, useState, useCallback } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { TestBadge } from "@/components/common";
import { IntegrationsAdmin } from "@/components/IntegrationsAdmin";
import { RolesTab, TagsTab, FieldsTab, ConnectionsTab, SheetsTab, UatTab, OpsConfigTab } from "@/components/AdminExtras";
import { VoiceMonitorTab } from "@/components/VoiceMonitor";
import { KnowledgeLibraryTab } from "@/components/KnowledgeLibrary";
import { BillingSubscriptionsTab } from "@/components/BillingSubscriptions";
import { AutomationsTab } from "@/components/AutomationBuilder";
import { FinanceAdminTab } from "@/components/FinanceAdmin";
import { MetaAdminTab } from "@/components/MetaAdmin";
import { AiIntelligenceTab } from "@/components/AiIntelligenceAdmin";
import { DataIntegrityTab } from "@/components/DataIntegrityAdmin";
import { TaskCleanupTab } from "@/components/TaskCleanupAdmin";
import { WebinarTab } from "@/components/WebinarAdmin";
import { WORKFLOW_SETUP_ADMIN_ROUTE } from "@/components/WorkflowSetupRequired";
import { Download, UserPlus, ShieldAlert, Database, HardDrive, RefreshCcw, ExternalLink, FolderCheck, KeyRound, CheckCircle2, XCircle, MinusCircle, ListChecks, Clock } from "lucide-react";

const HEALTH_STYLES = {
  complete: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  pass: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  success: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  ok: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  partial: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30",
  incomplete: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30",
  failed: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30",
  fail: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30",
  error: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30",
  not_configured: "bg-muted text-muted-foreground border",
};
const HEALTH_ICON = {
  complete: CheckCircle2, pass: CheckCircle2, success: CheckCircle2, ok: CheckCircle2,
  partial: ShieldAlert, incomplete: XCircle, failed: XCircle, fail: XCircle, error: XCircle,
  not_configured: MinusCircle,
};
const HealthBadge = ({ status, label }) => {
  const key = status || "not_configured";
  const Icon = HEALTH_ICON[key] || MinusCircle;
  return (
    <Badge variant="outline" className={`border gap-1 ${HEALTH_STYLES[key] || "bg-muted text-muted-foreground"}`}>
      <Icon className="h-3 w-3" />{(label || key).toUpperCase().replace(/_/g, " ")}
    </Badge>
  );
};

/* Shared by the Database Backup and Employee Emergency Workbook Drive
   pickers — each has its OWN independent config key/endpoint, never a
   shared folder, and each surfaces the live folder name (not just an ID)
   so an admin never sees "a folder is configured" without seeing where. */
const DriveFolderCard = ({ label, description, getPath, postPath, testPrefix }) => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.get(getPath).then((r) => setData(r.data)).catch(() => setData(null)), [getPath]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!input.trim()) return;
    setSaving(true);
    try {
      const { data: res } = await api.post(postPath, { folder: input.trim() });
      toast.success(`Saved — now using "${res.folder_name || res.folder_id}"`);
      setInput(""); setEditing(false); load();
    } catch (e) { toast.error(errDetail(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-2 rounded-md border p-3">
      <Label className="text-xs font-medium">{label}</Label>
      {description && <p className="text-[11px] text-muted-foreground">{description}</p>}
      {data?.folder_id && !editing ? (
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 text-xs flex-wrap">
            <FolderCheck className="h-3.5 w-3.5 text-[hsl(var(--success))]" />
            <span className="font-medium">{data.folder_name || "(name unavailable — link/ID still work)"}</span>
            {!data.is_explicit && <Badge variant="outline" className="text-[9px]">auto-created default</Badge>}
            {data.connection_status !== "connected" && (
              <Badge variant="outline" className="text-[9px] text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30">Google not connected</Badge>
            )}
          </div>
          <p className="text-[10px] font-mono text-muted-foreground truncate">{data.folder_id}</p>
          <div className="flex gap-2">
            <a href={data.folder_link} target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`${testPrefix}-open`}>
                <ExternalLink className="h-3 w-3 mr-1" /> Open folder
              </Button>
            </a>
            <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`${testPrefix}-change`} onClick={() => setEditing(true)}>Change folder</Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Input data-testid={`${testPrefix}-input`} placeholder="Paste a Google Drive folder link or ID…" className="h-9 text-xs" value={input} onChange={(e) => setInput(e.target.value)} />
          <Button data-testid={`${testPrefix}-save`} size="sm" disabled={saving || !input.trim()} onClick={save}>{saving ? "Checking…" : "Save"}</Button>
          {data?.folder_id && <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setInput(""); }}>Cancel</Button>}
        </div>
      )}
    </div>
  );
};

const DEFAULT_ROLES = ["admin", "manager", "supervisor", "counsellor", "process", "backoffice"];

// 2026-09 Section 6 fix — every internal Tabs `value` this page actually
// renders a TabsTrigger/TabsContent pair for (kept in exact sync with the
// JSX below on purpose, so a newly added tab is caught by
// AdminPage.deepLink.test.jsx if its value is forgotten here). Several of
// these are one-word slugs (`dataintegrity`, `opsconfig`, `testdata`...)
// that don't match the natural hyphenated spelling of their own label —
// `?tab=data-integrity` (the reported deep-link bug) is the exact
// mismatch: the URL param never equalled any real tab value, so Radix
// Tabs had nothing to select and rendered no content at all.
const ADMIN_TAB_VALUES = [
  "users", "roles", "integrations", "connections", "sheets", "fields", "tags",
  "automations", "meta", "ai", "finance", "opsconfig", "voice", "knowledge",
  "webinar", "billing", "uat", "audit", "export", "testdata", "dataintegrity", "taskcleanup",
];
// Strips everything but letters/digits so "data-integrity", "data_integrity"
// and "dataintegrity" all normalize to the same slug — never hand-list every
// separator variant per tab.
const normalizeAdminTabSlug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const resolveAdminTab = (raw) => {
  if (!raw) return null;
  if (ADMIN_TAB_VALUES.includes(raw)) return raw;
  const norm = normalizeAdminTabSlug(raw);
  return ADMIN_TAB_VALUES.find((v) => normalizeAdminTabSlug(v) === norm) || null;
};
// The old "Legacy Case adoption" tab is gone (see the redirect below) but is
// not a currently-supported value, so it's deliberately kept OUT of
// ADMIN_TAB_VALUES/resolveAdminTab and matched separately.
const isLegacyCaseAdoptionTab = (raw) => normalizeAdminTabSlug(raw) === "caseadoption";

const ResetPasswordDialog = ({ target, open, setOpen }) => {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setPassword(""); }, [open]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${target.user_id}/set-password`, { password });
      toast.success(`Password set for ${target.email}`);
      setOpen(false);
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  if (!target) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-sm" data-testid="reset-password-dialog">
        <DialogHeader><DialogTitle className="font-heading">Set password — {target.name}</DialogTitle></DialogHeader>
        <div className="space-y-1.5">
          <Label className="text-xs">New password (min. 8 characters)</Label>
          <Input data-testid="reset-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          <p className="text-[11px] text-muted-foreground">{target.email} can log in with this immediately — existing Google sign-in (if any) keeps working too.</p>
        </div>
        <DialogFooter>
          <Button data-testid="reset-password-confirm" disabled={password.length < 8 || busy} onClick={submit}>{busy ? "Saving…" : "Set password"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UsersTab = () => {
  const [data, setData] = useState(null);
  const [roles, setRoles] = useState(DEFAULT_ROLES);
  const [form, setForm] = useState({ email: "", name: "", role: "counsellor", division: "Sales", password: "" });
  const [open, setOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);

  const load = useCallback(() => api.get("/users").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/users/roles").then((r) => setRoles(r.data.map((x) => x.role))).catch(() => {}); }, []);

  const create = async () => {
    try {
      const body = { ...form, password: form.password || undefined };
      await api.post("/users", body);
      toast.success(form.password ? "User provisioned with email/password sign-in" : "User provisioned — they can sign in with Google");
      setOpen(false);
      setForm({ email: "", name: "", role: "counsellor", division: "Sales", password: "" });
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const update = async (userId, updates) => {
    try {
      await api.patch(`/users/${userId}`, updates);
      toast.success("User updated");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  if (!data) return null;
  return (
    <div className="space-y-4">
      {data.pending.length > 0 && (
        <Card className="border-[hsl(var(--warning))]/40">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Pending access requests</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {data.pending.map((p) => (
              <div key={p.email} className="flex items-center gap-3 text-sm" data-testid={`pending-user-${p.email}`}>
                <span className="flex-1">{p.name || p.email} <span className="text-muted-foreground">({p.email})</span></span>
                <Select onValueChange={(role) => api.post("/users", { email: p.email, name: p.name || p.email, role }).then(() => { toast.success("Approved"); load(); })}>
                  <SelectTrigger className="w-40 h-8"><SelectValue placeholder="Approve as…" /></SelectTrigger>
                  <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="admin-add-user-button"><UserPlus className="h-4 w-4 mr-1.5" /> Provision user</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Provision employee</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Email</Label><Input data-testid="admin-user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Name</Label><Input data-testid="admin-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="admin-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Division</Label><Input value={form.division} onChange={(e) => setForm({ ...form, division: e.target.value })} /></div>
              </div>
              <div className="space-y-1.5">
                <Label>Password (optional)</Label>
                <Input data-testid="admin-user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Leave blank for Google sign-in only" />
                <p className="text-[11px] text-muted-foreground">Set this to also let them log in with email + password, min. 8 characters. Their Google account (once approved) works either way.</p>
              </div>
            </div>
            <DialogFooter><Button data-testid="admin-user-create-button" onClick={create} disabled={!form.email || !form.name || (form.password && form.password.length < 8)}>Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead><TableHead>Role</TableHead><TableHead>Division</TableHead><TableHead>Active</TableHead><TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.users.map((u) => (
              <TableRow key={u.user_id} data-testid={`admin-user-row-${u.email}`}>
                <TableCell>
                  <div className="flex items-center gap-2"><span className="font-medium text-sm">{u.name}</span><TestBadge show={u.is_test} /></div>
                  <span className="text-xs text-muted-foreground">{u.email}</span>
                </TableCell>
                <TableCell>
                  <Select value={u.role} onValueChange={(role) => update(u.user_id, { role })}>
                    <SelectTrigger className="w-36 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-sm">{u.division || "—"}</TableCell>
                <TableCell>
                  <Switch data-testid={`admin-user-active-${u.email}`} checked={u.is_active} onCheckedChange={(v) => update(u.user_id, { is_active: v })} />
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" data-testid={`admin-user-reset-password-${u.email}`} onClick={() => setResetTarget(u)}>
                    <KeyRound className="h-3.5 w-3.5 mr-1" />Password
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <ResetPasswordDialog target={resetTarget} open={!!resetTarget} setOpen={(o) => !o && setResetTarget(null)} />
    </div>
  );
};

const IntegrationsTab = () => {
  const [health, setHealth] = useState(null);
  useEffect(() => { api.get("/admin/integration-health").then((r) => setHealth(r.data)); }, []);
  return (
    <div className="space-y-4">
      <IntegrationsAdmin />
      {health && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Background jobs</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <p>Pending jobs: <b data-testid="jobs-pending-count">{health.scheduler.pending_jobs}</b></p>
            <p>Failed jobs: <b data-testid="jobs-failed-count">{health.scheduler.failed_jobs}</b></p>
            {health.last_export && <p className="text-xs text-muted-foreground">Last export: {health.last_export.entity} · {health.last_export.rows} rows · {fmtIST(health.last_export.at)} by {health.last_export.by}</p>}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const BackupDetailsDialog = ({ backup, open, setOpen }) => {
  if (!backup) return null;
  const manifest = backup.manifest || [];
  const failing = manifest.filter((m) => m.status !== "pass");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="backup-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            Backup details <HealthBadge status={backup.status} />
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-xs">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-md border p-3 space-y-1">
              <p className="font-medium flex items-center gap-1.5"><HardDrive className="h-3.5 w-3.5" /> Local storage</p>
              <HealthBadge status={backup.local_status || "success"} label={backup.local_status === "failed" ? "Failed" : "Success"} />
              {backup.local_error && <p className="text-[11px] text-[hsl(var(--danger))]">{backup.local_error}</p>}
            </div>
            <div className="rounded-md border p-3 space-y-1">
              <p className="font-medium flex items-center gap-1.5"><FolderCheck className="h-3.5 w-3.5" /> Google Drive</p>
              <HealthBadge status={backup.drive_status || "not_configured"}
                label={backup.drive_status === "success" ? "Success" : backup.drive_status === "failed" ? "Failed" : "Not connected"} />
              {backup.drive_error && <p className="text-[11px] text-[hsl(var(--danger))]">{backup.drive_error}</p>}
              {backup.drive_folder_id && <p className="text-[10px] font-mono text-muted-foreground truncate">{backup.drive_folder_id}</p>}
              {backup.drive_account_email && <p className="text-[10px] text-muted-foreground">Account: {backup.drive_account_email}</p>}
              {backup.drive_file_id && <p className="text-[10px] font-mono text-muted-foreground truncate">File: {backup.drive_file_id}</p>}
            </div>
          </div>
          <div className="space-y-1">
            <p className="font-medium">Metadata</p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-muted-foreground">
              <p>Backup ID: <span className="font-mono text-foreground">{backup.backup_id}</span></p>
              <p>Created: <span className="text-foreground">{fmtIST(backup.created_at, { year: true })}</span></p>
              <p>Size: <span className="text-foreground">{(backup.size_bytes / 1024).toFixed(0)} KB</span></p>
              <p>Collections: <span className="text-foreground">{backup.collections}</span></p>
              <p>Secrets handling: <span className="text-foreground capitalize">{backup.secrets_handling || "—"}</span>
                {typeof backup.secrets_sanitized_count === "number" && ` (${backup.secrets_sanitized_count} keys)`}</p>
              <p>Zip integrity: <span className="text-foreground">{failing.some((m) => m.collection === "__zip_integrity__") ? "FAILED" : "verified"}</span></p>
            </div>
          </div>
          <div className="space-y-1.5">
            <p className="font-medium flex items-center gap-1.5"><ListChecks className="h-3.5 w-3.5" /> Collection coverage ({manifest.length})
              {failing.length > 0 && <span className="text-[hsl(var(--danger))] font-normal">— {failing.length} mismatch(es)</span>}</p>
            <div className="max-h-64 overflow-y-auto rounded-md border">
              <Table>
                <TableHeader><TableRow><TableHead>Collection</TableHead><TableHead>Source</TableHead><TableHead>Backed up</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {manifest.map((m) => (
                    <TableRow key={m.collection} data-testid={`manifest-row-${m.collection}`}>
                      <TableCell className="font-mono text-[11px]">{m.collection}</TableCell>
                      <TableCell>{m.source_count ?? "—"}</TableCell>
                      <TableCell>{m.backed_up_count ?? "—"}</TableCell>
                      <TableCell>
                        {m.status === "pass"
                          ? <HealthBadge status="pass" label="PASS" />
                          : <span className="flex flex-col gap-0.5"><HealthBadge status="fail" label="FAIL" />{m.detail && <span className="text-[10px] text-muted-foreground">{m.detail}</span>}</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const RestoreDialog = ({ backup, open, setOpen, onRestored }) => {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setTyped(""); }, [open]);
  const matches = backup && typed === backup.filename;
  const restore = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/backups/${backup.backup_id}/restore`, { confirm_filename: typed });
      toast.success(`Restored ${Object.keys(data.collections).length} collections. A safety backup of what was there before (${data.safety_backup_filename}) was taken automatically.`);
      setOpen(false);
      onRestored?.();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  if (!backup) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md" data-testid="restore-dialog">
        <DialogHeader><DialogTitle className="font-heading text-[hsl(var(--danger))] flex items-center gap-1.5"><ShieldAlert className="h-4 w-4" />Restore from backup</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            This <b>replaces every collection</b> in the live database with the contents of <code className="font-mono">{backup.filename}</code> ({fmtIST(backup.created_at, { year: true })}, {backup.collections} collections). A safety backup of the current state is taken automatically first, so this can always be undone by restoring that one — but nothing done in SalesOS since this backup was made will otherwise be recoverable.
          </p>
          <div className="space-y-1.5">
            <Label className="text-xs">Type the filename above exactly to confirm</Label>
            <Input data-testid="restore-confirm-input" className="font-mono text-xs h-9" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={backup.filename} />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="restore-confirm-button" variant="destructive" disabled={!matches || busy} onClick={restore}>
            {busy ? "Restoring…" : "Restore — overwrite live data"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BackupsSection = () => {
  const [rows, setRows] = useState(null);
  const [running, setRunning] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [detailsTarget, setDetailsTarget] = useState(null);
  const load = useCallback(() => api.get("/admin/backups").then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  const runNow = async () => {
    setRunning(true);
    try { await api.post("/admin/backups/run"); toast.success("Backup created"); load(); }
    catch (e) { toast.error(errDetail(e)); } finally { setRunning(false); }
  };
  const download = (b) => { window.open(`${api.defaults.baseURL}/admin/backups/${b.backup_id}/download`, "_blank"); };
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm font-heading flex items-center gap-1.5"><HardDrive className="h-4 w-4" />Database Backup (rolling 7 days)</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">A full backup runs automatically every day and is kept for 7 days — locally always, and in Google Drive too once connected. Restoring below overwrites live data, so it always takes a fresh safety backup first; <code className="font-mono">restore_backup.py</code> is also available as a command-line alternative (e.g. onto a rebuilt server where SalesOS itself isn't running yet).</p>
        <DriveFolderCard label="Database Backup Google Drive folder"
          description="Independent from the Employee Emergency Workbook's folder below — backups never silently share it."
          getPath="/admin/backups/drive-folder" postPath="/admin/backups/drive-folder" testPrefix="backup-drive-folder" />
        <Button data-testid="backup-run-now" size="sm" variant="outline" disabled={running} onClick={runNow}>{running ? "Running…" : "Back up now"}</Button>
        {rows && rows.length > 0 && (
          <Table>
            <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Size</TableHead><TableHead>Collections</TableHead><TableHead>Status</TableHead><TableHead>Stored</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {rows.map((b) => (
                <TableRow key={b.backup_id} data-testid={`backup-row-${b.backup_id}`}>
                  <TableCell className="text-xs">{fmtIST(b.created_at, { year: true })}</TableCell>
                  <TableCell className="text-xs">{(b.size_bytes / 1024).toFixed(0)} KB</TableCell>
                  <TableCell className="text-xs">{b.collections}</TableCell>
                  <TableCell className="text-xs"><HealthBadge status={b.status} /></TableCell>
                  <TableCell className="text-xs">
                    {b.file_status === "pruned" ? <Badge variant="outline">pruned</Badge> : "local"}
                    {b.drive_status === "success"
                      ? <a href={b.drive_link} target="_blank" rel="noreferrer" className="ml-1.5 text-primary hover:underline">+ Drive</a>
                      : b.drive_status === "failed"
                        ? <span className="ml-1.5 text-[hsl(var(--danger))]">Drive upload failed</span>
                        : null}
                  </TableCell>
                  <TableCell className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`backup-details-${b.backup_id}`} onClick={() => setDetailsTarget(b)}>Details</Button>
                    {b.file_status !== "pruned" && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => download(b)}><Download className="h-3.5 w-3.5" /></Button>
                    )}
                    {(b.file_status !== "pruned" || b.drive_link) && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs text-[hsl(var(--danger))] hover:text-[hsl(var(--danger))]" data-testid={`restore-open-${b.backup_id}`} onClick={() => setRestoreTarget(b)}>Restore</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      <RestoreDialog backup={restoreTarget} open={!!restoreTarget} setOpen={(o) => !o && setRestoreTarget(null)} onRestored={load} />
      <BackupDetailsDialog backup={detailsTarget} open={!!detailsTarget} setOpen={(o) => !o && setDetailsTarget(null)} />
    </Card>
  );
};

const TabHealthList = ({ detail }) => {
  const [open, setOpen] = useState(false);
  if (!detail || detail.length === 0) return null;
  const failing = detail.filter((d) => d.status !== "ok");
  return (
    <div className="space-y-1.5">
      <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[11px]" onClick={() => setOpen((o) => !o)}>
        {open ? "Hide" : "Show"} per-tab health ({detail.length - failing.length}/{detail.length} OK)
      </Button>
      {open && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] rounded-md border p-2">
          {detail.map((d) => (
            <div key={d.tab} className="flex items-center justify-between gap-2">
              <span className="truncate">{d.tab}</span>
              <HealthBadge status={d.status === "ok" ? "pass" : "fail"} label={d.status === "ok" ? `${d.rows} rows` : "ERROR"} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const EmployeeSheetSection = () => {
  const [status, setStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const load = useCallback(() => api.get("/admin/backups/sheets-status").then((r) => setStatus(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/backups/sheets-refresh");
      toast[data.status === "complete" ? "success" : "warning"](
        `Refresh finished: ${data.status.toUpperCase()} (${Object.values(data.rows || {}).length} tabs)`);
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setRefreshing(false); }
  };
  const toggle = async (enabled) => {
    try { await api.post("/admin/backups/sheets-toggle", { enabled }); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };
  if (!status) return null;
  const attempt = status.last_refresh_attempt || {};
  // The loop writes a heartbeat every ~20s — anything older than 2 minutes
  // means the scheduler process itself is very likely not running at all
  // (a genuinely different failure mode from "it's running but this one
  // job type keeps failing," which last_refresh_attempt already covers).
  const heartbeatStale = !status.scheduler_heartbeat_at
    || (Date.now() - new Date(status.scheduler_heartbeat_at).getTime()) > 120000;
  return (
    <Card data-testid="employee-workbook-admin">
      <CardHeader className="pb-2"><CardTitle className="text-sm font-heading flex items-center gap-1.5"><Database className="h-4 w-4" />Employee Emergency Workbook</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">A live Google Sheet mirroring the active working set across 15 tabs — leads, consultations, tasks, finance, calls, process cases, approvals, support, notes and more — so staff can keep working from it if SalesOS itself is unreachable. Rotates across 3 snapshots so a bad refresh never destroys the last good copy.</p>
        <div className="flex items-center gap-3 text-xs flex-wrap">
          <div className="flex items-center gap-2">
            <Switch data-testid="sheets-export-toggle" checked={!!status.enabled} onCheckedChange={toggle} />
            <span>{status.enabled ? "Enabled" : "Disabled"}</span>
          </div>
          <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3" /> Schedule: {status.schedule === "hourly" ? "Hourly" : status.schedule}</Badge>
        </div>

        <DriveFolderCard label="Employee Emergency Workbook Google Drive folder"
          description="Independent from the Database Backup folder above — this workbook never silently shares it. Changing this now safely MOVES the existing Snapshot 1/2/3 files into the new folder (never destroyed, never recreated fresh)."
          getPath="/admin/backups/sheets-drive-folder" postPath="/admin/backups/sheets-drive-folder" testPrefix="sheets-drive-folder" />

        <p className="text-[11px] text-muted-foreground rounded-md border p-2 bg-muted/40">
          TEST records are excluded from every tab of this workbook — only real customer/opportunity data ever appears here.
        </p>

        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs rounded-md border p-3">
          <p className="text-muted-foreground">Scheduler heartbeat</p>
          <p>
            {status.scheduler_heartbeat_at ? (
              <>
                {fmtIST(status.scheduler_heartbeat_at, { year: true })}{" "}
                <Badge variant="outline" className={heartbeatStale
                  ? "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30"
                  : "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30"}>
                  {heartbeatStale ? "STALE" : "ALIVE"}
                </Badge>
                <span className="text-muted-foreground"> — proves the scheduler loop is running, NOT that this job executes (see below)</span>
              </>
            ) : "Never seen"}
          </p>
          <p className="text-muted-foreground">Last successful refresh</p>
          <p>{status.last_good_refreshed ? fmtIST(status.last_good_refreshed, { year: true }) : "Never"} <span className="text-muted-foreground">(either source)</span></p>
          <p className="text-muted-foreground">Current workbook</p>
          <p>
            {status.link ? (
              <a href={status.link} target="_blank" rel="noreferrer" className="text-primary hover:underline flex items-center gap-1 w-fit">
                <ExternalLink className="h-3 w-3" /> Open current workbook
              </a>
            ) : "Not available yet — no refresh has fully succeeded"}
          </p>
          <p className="text-muted-foreground">Target (next) snapshot</p>
          <p>Snapshot {status.target_snapshot_number}{status.target_spreadsheet_id && <span className="font-mono text-muted-foreground"> ({status.target_spreadsheet_id.slice(0, 10)}…)</span>}</p>
          <p className="text-muted-foreground">Current Drive folder</p>
          <p className="font-mono truncate">{status.current_folder_id || "Not configured"}</p>
          {status.last_failure_reason && (
            <>
              <p className="text-muted-foreground">Last failure reason</p>
              <p className="text-[hsl(var(--danger))]">{status.last_failure_reason}</p>
            </>
          )}
        </div>

        <div className="rounded-md border p-3 space-y-2" data-testid="workbook-manual-vs-scheduled">
          <p className="text-xs font-medium">Manual vs. scheduled refresh — kept genuinely separate so a "Refresh now" click can never make the scheduler look healthy</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="rounded-md border p-2 space-y-1 text-[11px]">
              <p className="font-medium">Last MANUAL refresh (admin click)</p>
              <p>{status.last_manual_refresh?.attempted_at ? fmtIST(status.last_manual_refresh.attempted_at, { year: true }) : "Never"} {status.last_manual_refresh?.status && <HealthBadge status={status.last_manual_refresh.status} />}</p>
            </div>
            <div className="rounded-md border p-2 space-y-1 text-[11px]" data-testid="workbook-last-scheduled-refresh">
              <p className="font-medium">Last SCHEDULED refresh (durable hourly job)</p>
              <p>{status.last_scheduled_refresh?.attempted_at ? fmtIST(status.last_scheduled_refresh.attempted_at, { year: true }) : "Never — the hourly job has not completed a run yet"} {status.last_scheduled_refresh?.status && <HealthBadge status={status.last_scheduled_refresh.status} />}</p>
            </div>
          </div>
          <p className="text-xs font-medium pt-1">Durable scheduled_jobs record (ground truth — not the config keys above)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
            <div className="rounded-md border p-2 space-y-0.5">
              <p className="text-muted-foreground">Pending job</p>
              {status.scheduled_job_pending ? (
                <>
                  <p className="font-mono truncate">{status.scheduled_job_pending.job_id}</p>
                  <p>Scheduled for: {fmtIST(status.scheduled_job_pending.scheduled_run_at, { year: true })}</p>
                </>
              ) : <p>None pending</p>}
            </div>
            <div className="rounded-md border p-2 space-y-0.5">
              <p className="text-muted-foreground">Last completed job</p>
              {status.scheduled_job_last_completed ? (
                <>
                  <p className="font-mono truncate">{status.scheduled_job_last_completed.job_id}</p>
                  <p>Scheduled: {fmtIST(status.scheduled_job_last_completed.scheduled_run_at, { year: true })}</p>
                  <p>Claimed: {status.scheduled_job_last_completed.claimed_at ? fmtIST(status.scheduled_job_last_completed.claimed_at, { year: true }) : "—"}</p>
                  <p>Completed: {status.scheduled_job_last_completed.completed_at ? fmtIST(status.scheduled_job_last_completed.completed_at, { year: true }) : "—"}</p>
                  <p>Status: <HealthBadge status={status.scheduled_job_last_completed.status === "done" ? "pass" : "fail"} label={status.scheduled_job_last_completed.status} /></p>
                </>
              ) : <p className="text-[hsl(var(--warning))]">No scheduled_jobs run has ever completed — the hourly job has not yet been proven to execute</p>}
            </div>
          </div>
          {status.scheduled_job_last_failure && (
            <p className="text-[11px] text-[hsl(var(--danger))]">Last scheduler-level failure: {status.scheduled_job_last_failure.last_error} ({fmtIST(status.scheduled_job_last_failure.completed_at, { year: true })})</p>
          )}
        </div>

        <TabHealthList detail={attempt.detail} />
        {status.record_counts_by_tab && Object.keys(status.record_counts_by_tab).length > 0 && (
          <div className="space-y-1">
            <Label className="text-xs">Record counts per tab (last refresh)</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-0.5 text-[11px] rounded-md border p-2">
              {Object.entries(status.record_counts_by_tab).map(([tab, count]) => (
                <div key={tab} className="flex items-center justify-between gap-2">
                  <span className="truncate text-muted-foreground">{tab}</span>
                  <span className="font-mono">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {status.coverage_diagnostics && (
          <div className="rounded-md border p-3 space-y-2" data-testid="workbook-coverage-diagnostics">
            <p className="text-xs font-medium">Leads & Opportunities production coverage audit (latest refresh)</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1 text-[11px]">
              <p><span className="text-muted-foreground">Total contacts in source:</span> {status.coverage_diagnostics.total_contacts_in_source}</p>
              <p><span className="text-muted-foreground">Non-TEST contacts:</span> {status.coverage_diagnostics.total_contacts_non_test}</p>
              <p><span className="text-muted-foreground">Total opportunities:</span> {status.coverage_diagnostics.total_opportunities_in_source}</p>
              <p><span className="text-muted-foreground">Non-TEST opportunities:</span> {status.coverage_diagnostics.total_opportunities_non_test}</p>
              <p><span className="text-muted-foreground">Exported opportunity rows:</span> {status.coverage_diagnostics.exported_opportunity_rows}</p>
              <p><span className="text-muted-foreground">Exported contact-only rows:</span> {status.coverage_diagnostics.exported_contact_only_rows}</p>
              <p className="font-medium col-span-2 sm:col-span-3"><span className="text-muted-foreground font-normal">Total rows exported:</span> {status.coverage_diagnostics.exported_total_rows}</p>
            </div>
            <p className="text-[11px] font-medium">Excluded, by reason</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
              <p>Contacts — TEST record: {status.coverage_diagnostics.excluded_contacts?.test_record}</p>
              <p>Contacts — archived: {status.coverage_diagnostics.excluded_contacts?.archived}</p>
              <p>Contacts — merged: {status.coverage_diagnostics.excluded_contacts?.merged}</p>
              <p>Opportunities — TEST record: {status.coverage_diagnostics.excluded_opportunities?.test_record}</p>
              <p>Opportunities — ineligible contact: {status.coverage_diagnostics.excluded_opportunities?.belongs_to_ineligible_contact}</p>
            </div>
            <p className="text-[10px] text-muted-foreground">No filter applied for: {(status.coverage_diagnostics.no_filter_applied_for || []).join(", ")} — computed {fmtIST(status.coverage_diagnostics.computed_at, { year: true })}</p>
          </div>
        )}

        {status.snapshots && status.snapshots.length > 0 && (
          <div className="space-y-1.5">
            <Label className="text-xs">Active snapshots — Drive location verification</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {status.snapshots.map((s) => (
                <div key={s.slot} className="rounded-md border p-2 space-y-1 text-[11px]" data-testid={`workbook-snapshot-${s.slot}`}>
                  <p className="font-medium flex items-center gap-1.5">
                    Snapshot {s.slot} {s.is_current && <Badge variant="outline" className="text-[9px]">current</Badge>}
                  </p>
                  <HealthBadge status={s.status || "not_configured"} label={s.status ? undefined : "never run"} />
                  <p className="text-muted-foreground">{s.refreshed_at ? fmtIST(s.refreshed_at, { year: true }) : "Never refreshed"}</p>
                  <p className="font-mono truncate" title={s.spreadsheet_id}>{s.spreadsheet_id}</p>
                  <div className="flex items-center gap-1">
                    {s.parent_verified === true ? (
                      <Badge variant="outline" className="text-[9px] bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30">Parent verified: YES</Badge>
                    ) : s.parent_verified === false ? (
                      <Badge variant="outline" className="text-[9px] bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30">Parent verified: NO</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[9px]">Parent: unknown{s.parent_check_error ? ` (${s.parent_check_error})` : ""}</Badge>
                    )}
                  </div>
                  {s.parent_verified === false && (
                    <p className="text-[10px] text-[hsl(var(--danger))] font-mono truncate" title={s.actual_parent_folder_id}>actual: {s.actual_parent_folder_id || "—"}</p>
                  )}
                  <a href={s.link} target="_blank" rel="noreferrer" className="text-primary hover:underline flex items-center gap-1 w-fit">
                    <ExternalLink className="h-3 w-3" /> Open
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        <Button data-testid="sheets-refresh-now" size="sm" variant="outline" disabled={refreshing} onClick={refresh}>
          <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />{refreshing ? "Refreshing…" : "Refresh now (manual)"}
        </Button>
      </CardContent>
    </Card>
  );
};

const ExportTab = () => {
  const entities = ["contacts", "leads", "opportunities", "appointments", "tasks"];
  const download = async (entity) => {
    try {
      const res = await api.get(`/admin/export?entity=${entity}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `salesos_${entity}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${entity} exported`);
    } catch (e) { toast.error(errDetail(e)); }
  };
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading">Emergency operational export</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">Read-only fallback dataset (CSV, on demand). TEST records excluded.</p>
          <div className="flex flex-wrap gap-2">
            {entities.map((e) => (
              <Button key={e} data-testid={`export-${e}-button`} variant="outline" size="sm" onClick={() => download(e)}>
                <Download className="h-3.5 w-3.5 mr-1.5" /> {e}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
      <BackupsSection />
      <EmployeeSheetSection />
    </div>
  );
};

const AuditTab = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/audit?limit=100").then((r) => setItems(r.data)); }, []);
  if (!items) return null;
  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow><TableHead>When</TableHead><TableHead>Actor</TableHead><TableHead>Action</TableHead><TableHead className="hidden md:table-cell">Record</TableHead><TableHead className="hidden lg:table-cell">Detail</TableHead></TableRow>
        </TableHeader>
        <TableBody>
          {items.map((a) => (
            <TableRow key={a.audit_id}>
              <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtIST(a.at, { year: true })}</TableCell>
              <TableCell className="text-xs font-mono">{a.actor?.slice(0, 14)}</TableCell>
              <TableCell><Badge variant="outline" className="text-[11px]">{a.action}</Badge></TableCell>
              <TableCell className="hidden md:table-cell text-xs font-mono text-muted-foreground">{a.record_type}:{String(a.record_id).slice(0, 10)}</TableCell>
              <TableCell className="hidden lg:table-cell text-xs text-muted-foreground max-w-64 truncate">{a.reason || (a.new_value ? JSON.stringify(a.new_value).slice(0, 60) : "")}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

const TestDataTab = () => {
  const [busy, setBusy] = useState(false);
  const archive = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/archive-test-data");
      toast.success(`Archived ${data.archived_contacts} TEST contacts, ${data.archived_opportunities} opportunities`);
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  const reseed = async () => {
    setBusy(true);
    try {
      await api.post("/admin/seed-test-data?force=true");
      toast.success("TEST scenarios re-seeded");
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm font-heading flex items-center gap-2"><Database className="h-4 w-4" /> Synthetic TEST data</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">TEST records carry a purple badge, never trigger external actions, and are excluded from KPIs by default. Archiving never touches real customer data.</p>
        <div className="flex gap-2">
          <Button data-testid="seed-test-data-button" variant="outline" size="sm" disabled={busy} onClick={reseed}>Re-seed test scenarios</Button>
          <Button data-testid="archive-test-data-button" variant="outline" size="sm" disabled={busy} onClick={archive}>Archive all TEST records</Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default function AdminPage() {
  const { hasCap } = useAuth();
  const [params, setParams] = useSearchParams();
  const googleResult = params.get("google");
  // ?tab=<value> lets other admin pages deep-link here (e.g. Integrations'
  // compact ElevenLabs status card linking to the full Voice & ElevenLabs
  // page) without hardcoding a second navigation mechanism. Resolved
  // through resolveAdminTab so a URL-friendly spelling (`data-integrity`)
  // round-trips to the real internal tab value (`dataintegrity`) instead of
  // landing on a value no TabsTrigger/TabsContent actually has.
  const [tab, setTab] = useState(() => {
    if (googleResult) return "integrations";
    const raw = params.get("tab");
    if (isLegacyCaseAdoptionTab(raw)) return "caseadoption";
    return resolveAdminTab(raw) || "users";
  });

  // Reactive to a `?tab=` change on an ALREADY-mounted AdminPage too, not
  // just a fresh page load — otherwise a same-page navigate() from a child
  // (e.g. Integrations' "Manage Voice & ElevenLabs" link) updates the URL
  // but the tab state (only ever read once, at mount) never follows it.
  useEffect(() => {
    const raw = params.get("tab");
    if (!raw) return;
    const requested = isLegacyCaseAdoptionTab(raw) ? "caseadoption" : resolveAdminTab(raw);
    if (requested && requested !== tab) {
      setTab(requested);
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (!googleResult) return;
    if (googleResult === "connected") {
      toast.success("Google Workspace connected — Calendar & Meet sync enabled");
    } else {
      toast.error(`Google connection failed${params.get("reason") ? `: ${params.get("reason")}` : ""}`);
    }
    setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2026-09 — the old "Legacy Case adoption" tab is gone; its job is done by
  // Process / Cases -> Existing Case Workflow Setup. An old bookmarked
  // ?tab=caseadoption link lands there instead of on an empty tab.
  if (tab === "caseadoption") return <Navigate to={WORKFLOW_SETUP_ADMIN_ROUTE} replace />;

  return (
    <div className="space-y-4" data-testid="admin-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Administration</h1>
        <p className="text-sm text-muted-foreground">Users, roles & permissions, integrations, webhooks, custom fields, tags, automations, operational policies, UAT feedback, audit and test data.</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto flex-wrap justify-start gap-0.5">
          <TabsTrigger data-testid="admin-tab-users" value="users">Users</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-roles" value="roles">Roles & permissions</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-integrations" value="integrations">Integrations</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-connections" value="connections">Webhooks & API</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-sheets" value="sheets">Sheets intake</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-fields" value="fields">Custom fields</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-tags" value="tags">Tags</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-automations" value="automations">Automations</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-meta" value="meta">Meta / Facebook</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-ai" value="ai">AI Intelligence</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-finance" value="finance">Commercial plans</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-opsconfig" value="opsconfig">Ops policies</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-voice" value="voice">Voice & ElevenLabs</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-knowledge" value="knowledge">Knowledge & Policies</TabsTrigger>
          {(hasCap("webinar.view") || hasCap("webinar.config")) && (
            <TabsTrigger data-testid="admin-tab-webinar" value="webinar">Webinar</TabsTrigger>
          )}
          <TabsTrigger data-testid="admin-tab-billing" value="billing">Billing & Subscriptions</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-uat" value="uat">UAT feedback</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-audit" value="audit">Audit log</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-export" value="export">Export</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-testdata" value="testdata">Test data</TabsTrigger>
          {hasCap("admin.data_integrity") && (
            <TabsTrigger data-testid="admin-tab-dataintegrity" value="dataintegrity">Data Integrity</TabsTrigger>
          )}
          {hasCap("admin.config") && (
            <TabsTrigger data-testid="admin-tab-taskcleanup" value="taskcleanup">Task cleanup</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="roles"><RolesTab /></TabsContent>
        <TabsContent value="integrations"><IntegrationsTab /></TabsContent>
        <TabsContent value="connections"><ConnectionsTab /></TabsContent>
        <TabsContent value="sheets"><SheetsTab /></TabsContent>
        <TabsContent value="fields"><FieldsTab /></TabsContent>
        <TabsContent value="tags"><TagsTab /></TabsContent>
        <TabsContent value="automations"><AutomationsTab /></TabsContent>
        <TabsContent value="meta"><MetaAdminTab /></TabsContent>
        <TabsContent value="ai"><AiIntelligenceTab /></TabsContent>
        <TabsContent value="finance"><FinanceAdminTab /></TabsContent>
        <TabsContent value="opsconfig"><OpsConfigTab /></TabsContent>
        <TabsContent value="voice"><VoiceMonitorTab /></TabsContent>
        <TabsContent value="knowledge"><KnowledgeLibraryTab /></TabsContent>
        {(hasCap("webinar.view") || hasCap("webinar.config")) && (
          <TabsContent value="webinar"><WebinarTab /></TabsContent>
        )}
        <TabsContent value="billing"><BillingSubscriptionsTab /></TabsContent>
        <TabsContent value="uat"><UatTab /></TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
        <TabsContent value="export"><ExportTab /></TabsContent>
        <TabsContent value="testdata"><TestDataTab /></TabsContent>
        {hasCap("admin.data_integrity") && (
          <TabsContent value="dataintegrity"><DataIntegrityTab /></TabsContent>
        )}
        {hasCap("admin.config") && (
          <TabsContent value="taskcleanup"><TaskCleanupTab /></TabsContent>
        )}
      </Tabs>
    </div>
  );
}
